/**
 * The user's own Anthropic key, for the Director: theirs to enter, check,
 * replace and remove.
 *
 * Pasted into a password field, it is read for its shape (sk-ant-…) and kept
 * for this tab only: the session's sessionStorage slot, never localStorage,
 * wiped on Disconnect. It leaves the page only in the body of the user's own
 * Director runs and key checks, to this deployment, which hands it to
 * Anthropic for that run. A check asks Anthropic through the API
 * (`assistKeyCheck`: one page of its Models API, no tokens) and shows ✓ or ✕
 * in Anthropic's words. Wherever the key is shown it is ••••AbCd at most.
 *
 * One control, three homes: a chip in the Director's bar, the first run's
 * Director card, and a field on Connect under the key capsule.
 */

import { h } from '../../../console/src/dom.ts';
import type { AppContext, AssistKeySource } from '../contracts.ts';

/** AnthropicKey in the spec: sk-ant- then 16 to 256 of A–Z, a–z, 0–9, _ and -. */
export const ANTHROPIC_KEY = /^sk-ant-[A-Za-z0-9_-]{16,256}$/;

/** The key as the user may see it: its last four, never more. */
export const keyTail = (key: string): string => `••••${key.slice(-4)}`;

/** Why a pasted string is not an Anthropic key, or null when it reads as one. */
export const anthropicKeyProblem = (raw: string): string | null => {
  const s = raw.trim();
  if (s === '') return 'paste your Anthropic API key (sk-ant-…)';
  if (/^(sbk|sbo|sbv|whsec)_/.test(s)) return 'that is a schedule-bit secret, not an Anthropic key: it belongs in the key capsule';
  if (!s.startsWith('sk-ant-')) return 'an Anthropic API key starts with sk-ant-';
  if (!ANTHROPIC_KEY.test(s)) return 'an Anthropic API key is sk-ant- then 16 to 256 letters, digits, _ or -';
  return null;
};

/** Whose key the next run would spend: the user's, the deployment's, a dev deployment's script, or none at all. */
export type KeyUse = AssistKeySource | 'none';

/**
 * The chip: the user's key by its tail, else the deployment's, else an invitation. Where a
 * script plays in the model's place no key is spent, the user's included, so it names none.
 */
export const chipText = (own: string | null, deployment: boolean, scripted = false): string =>
  scripted ? `scripted: no key spent${own ? ` (yours ${keyTail(own)} kept)` : ''}`
    : own ? `your key ${keyTail(own)}` : deployment ? 'the deployment’s key' : 'no key — add yours';

/** The disclosure's first sentence: which key a run spends, and where the user's goes. */
export const keyLine = (use: KeyUse, own: string | null): string => {
  switch (use) {
    case 'own': return `A run uses your Anthropic key${own ? ` (${keyTail(own)})` : ''}: it goes to Anthropic through this deployment for that run only, and is kept nowhere but this tab.`;
    case 'deployment': return 'A run uses the deployment’s Anthropic key.';
    case 'scripted': return 'A script plays in the model’s place here, so no key is spent, yours included.';
    case 'none': return 'There is no Anthropic key to run on here: add yours.';
  }
};

/** What a check said, for the key it was about. */
export interface Checked { readonly key: string; readonly valid: boolean; readonly detail: string }

/** A check's line: ✓ or ✕ and Anthropic's words, only while it is about the key in hand. */
export const checkedLine = (c: Checked | null, key: string | null): string =>
  c && key !== null && c.key === key ? `${c.valid ? '✓' : '✕'} ${c.detail}` : '';

export interface KeyControl {
  readonly el: HTMLElement;
  /** Draw the chip and the rows again (the session or the config changed). */
  paint(): void;
  /** Open the box; with `focus`, the field takes focus (where no key is held yet). */
  open(focus?: boolean): void;
  /** A run said Anthropic refused the key: show it on the control, open. */
  rejected(detail: string): void;
  off(): void;
}

export interface KeyControlOptions {
  /** The deployment has a key of its own (or plays a script): a run needs none of the user's. */
  readonly deployment: () => boolean;
  /** A script plays in the model's place here: no key is spent, the user's included. */
  readonly scripted?: () => boolean;
  /** Always open, no chip (Connect's field). */
  readonly inline?: boolean;
  /** A check needs a workspace key to go with; without one it waits (Connect before connecting). */
  readonly canCheck?: () => boolean;
}

export const HINT = 'Kept in this tab only (sessionStorage), gone on Disconnect. Sent with your own Director runs to this deployment, which hands it to Anthropic for that run only and stores it nowhere.';

export const keyControl = (ctx: Pick<AppContext, 'session' | 'director' | 'announce'>, opts: KeyControlOptions): KeyControl => {
  let isOpen = opts.inline === true;
  let replacing = false;
  let checked: Checked | null = null;
  let checking = false;
  /** Why what was typed is not a key, until it is typed again. */
  let problem: string | null = null;
  const offs: (() => void)[] = [];

  const chip = h('button', { type: 'button', class: 'akey-chip', 'aria-expanded': 'false', title: 'Your Anthropic key for the Director: add, check, replace or remove it' });
  const input = h('input', {
    type: 'password', class: 'mono akey-input', placeholder: 'sk-ant-…', autocomplete: 'off', spellcheck: 'false', 'aria-label': 'Your Anthropic API key, for the Director',
  });
  const use = h('button', { type: 'button', class: 'akey-btn' }, 'use');
  const keep = h('button', { type: 'button', class: 'akey-btn ghost' }, 'keep it');
  const held = h('span', { class: 'mono akey-held' });
  const check = h('button', { type: 'button', class: 'akey-btn' }, 'check');
  const replace = h('button', { type: 'button', class: 'akey-btn ghost' }, 'replace');
  const remove = h('button', { type: 'button', class: 'akey-btn ghost' }, 'remove');
  // Not a live region: what it says is also announced, once, through the page's announcer.
  const status = h('p', { class: 'akey-status' });
  const entry = h('div', { class: 'akey-row' }, input, use, keep);
  const holding = h('div', { class: 'akey-row' }, held, check, replace, remove);
  const box = h('div', { class: 'akey-box', role: 'group', 'aria-label': 'Your Anthropic key' },
    h('p', { class: 'akey-eyebrow' }, 'YOUR ANTHROPIC KEY · for the Director'), entry, holding, status, h('p', { class: 'akey-hint' }, HINT));
  const el = h('div', { class: `akey${opts.inline ? ' inline' : ''}` }, opts.inline ? null : chip, box);

  const say = (text: string, tone: 'ok' | 'bad' | '' = ''): void => {
    status.textContent = text;
    status.dataset.tone = tone;
  };

  const paint = (): void => {
    const own = ctx.session.anthropicKey();
    const deployment = opts.deployment();
    el.dataset.state = own ? 'own' : deployment ? 'deployment' : 'none';
    // The chip carries the last check of the key in hand: ✓ taken, ✕ refused (or not asked).
    const verdict = own && checked?.key === own ? (checked.valid ? 'ok' : 'bad') : '';
    el.dataset.checked = verdict;
    chip.textContent = `${chipText(own, deployment, opts.scripted?.() ?? false)}${verdict === 'ok' ? ' ✓' : verdict === 'bad' ? ' ✕' : ''}`;
    chip.setAttribute('aria-expanded', String(isOpen));
    box.hidden = !isOpen;
    const entering = own === null || replacing;
    entry.hidden = !entering;
    holding.hidden = entering;
    keep.hidden = !(own && replacing);
    held.textContent = own ? `your key ${keyTail(own)}` : '';
    const checkable = opts.canCheck?.() ?? true;
    check.hidden = !checkable;
    // Never `disabled`: a focused button that is disabled drops focus to <body>, which closes the
    // Director's dock mid-check. While a check runs, a press is ignored instead (see `runCheck`).
    check.setAttribute('aria-disabled', String(checking));
    const line = checkedLine(checked, own);
    if (checking) say('asking Anthropic…');
    else if (problem && entering) say(problem, 'bad');
    else if (line && !entering) say(line, checked?.valid ? 'ok' : 'bad');
    else if (own && !entering && !checkable) say('Connect first to check it with Anthropic.');
    else say('');
  };

  const open = (focus = false): void => {
    isOpen = true;
    paint();
    if (focus && !entry.hidden) input.focus();
  };

  /** Where focus goes once a key is held: check, where it can be checked, else replace. Never left on a hidden control. */
  const focusHeld = (): void => {
    if (holding.hidden) return;
    (check.hidden ? replace : check).focus();
  };

  /** Back out of replacing: the held key stands, and focus lands on replace, beside it. */
  const keepHeld = (): void => {
    replacing = false;
    problem = null;
    input.value = '';
    paint();
    if (!holding.hidden) replace.focus();
  };

  const take = (): void => {
    const raw = input.value;
    problem = anthropicKeyProblem(raw);
    if (problem) { paint(); ctx.announce.assertive(problem); return; }
    input.value = '';
    replacing = false;
    checked = null;
    ctx.session.setAnthropicKey(raw.trim());
    ctx.announce.polite('Your Anthropic key is kept for this tab. The Director runs on it.');
    paint();
    focusHeld();
  };

  const runCheck = async (): Promise<void> => {
    const key = ctx.session.anthropicKey();
    if (!key || checking) return;
    checking = true;
    paint();
    const answer = await ctx.director.checkKey(key).catch(() => ({ problem: { detail: 'the check could not be sent' } }));
    checking = false;
    if ('problem' in answer) {
      const p = answer.problem;
      const detail = p && typeof p === 'object' && !Array.isArray(p) && typeof p.detail === 'string' ? p.detail : 'the check did not answer';
      checked = { key, valid: false, detail: `not checked: ${detail}` };
    } else {
      checked = { key, valid: answer.valid, detail: answer.detail };
    }
    // Said without the key: its tail is on the chip already.
    ctx.announce.polite(`${checked.valid ? 'Checked' : 'Not accepted'}: ${checked.detail}`);
    paint();
  };

  chip.addEventListener('click', () => { isOpen = !isOpen; paint(); if (isOpen && !entry.hidden) input.focus(); });
  use.addEventListener('click', take);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); take(); }
    else if (e.key === 'Escape' && replacing) { e.preventDefault(); keepHeld(); }
  });
  input.addEventListener('input', () => { if (problem) { problem = null; paint(); } });
  keep.addEventListener('click', keepHeld);
  check.addEventListener('click', () => void runCheck());
  replace.addEventListener('click', () => { replacing = true; paint(); input.focus(); });
  remove.addEventListener('click', () => {
    ctx.session.setAnthropicKey(null);
    checked = null;
    replacing = false;
    ctx.announce.polite('Your Anthropic key is removed from this tab.');
    paint();
    input.focus();
  });
  box.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || opts.inline || e.defaultPrevented) return;
    e.preventDefault();
    isOpen = false;
    paint();
    chip.focus();
  });
  offs.push(ctx.session.on(paint));
  paint();

  return {
    el, paint, open,
    rejected(detail) {
      const key = ctx.session.anthropicKey();
      if (key) checked = { key, valid: false, detail };
      open();
    },
    off() { for (const off of offs) off(); input.value = ''; },
  };
};
